import { supabase } from "../lib/supabase";

export type NovaOnlineServiceMode =
  | "dine-in"
  | "pickup"
  | "delivery";

export type NovaOnlineOrderLine = {
  itemId: string;
  variantId?: string | null;
  quantity: number;
};

export type NovaOnlineOrderInput = {
  serviceMode: NovaOnlineServiceMode;
  tableNumber?: string | null;

  guestName?: string | null;
  guestPhone?: string | null;

  deliveryAddress?: string | null;
  deliveryNotes?: string | null;

  paymentMethod?: string | null;

  lines: NovaOnlineOrderLine[];
};

export type NovaOnlineOrderConfirmation = {
  orderId: string;
  orderCode: string;
  currency: "KES";
  subtotal: number;
  total: number;
  status: string;
  serviceMode: NovaOnlineServiceMode;
};

function clean(
  value?: string | null,
) {
  const next =
    value?.trim() ?? "";

  return next || null;
}

export async function submitNovaOnlineOrder(
  input: NovaOnlineOrderInput,
): Promise<NovaOnlineOrderConfirmation> {
  if (!input.lines.length) {
    throw new Error(
      "Please add at least one item.",
    );
  }

  const { data, error } =
    await supabase.rpc(
      "create_nova_online_order",
      {
        p_service_mode:
          input.serviceMode,

        p_table_number:
          clean(
            input.tableNumber,
          ),

        p_guest_name:
          clean(
            input.guestName,
          ),

        p_guest_phone:
          clean(
            input.guestPhone,
          ),

        p_delivery_address:
          clean(
            input.deliveryAddress,
          ),

        p_delivery_notes:
          clean(
            input.deliveryNotes,
          ),

        p_payment_method:
          clean(
            input.paymentMethod,
          ),

        p_lines:
          input.lines.map(
            (line) => ({
              item_id:
                line.itemId,

              variant_id:
                line.variantId ??
                null,

              quantity:
                line.quantity,
            }),
          ),
      },
    );

  if (error) {
    throw new Error(
      error.message ||
        "Nova could not place this order.",
    );
  }

  const result =
    data as Record<
      string,
      unknown
    >;

  const orderCode =
    String(
      result.order_code ??
        "",
    ).trim();

  const orderId =
    String(
      result.order_id ??
        "",
    ).trim();

  if (!orderCode || !orderId) {
    throw new Error(
      "The order was not confirmed by Nova.",
    );
  }

  return {
    orderId,
    orderCode,

    currency: "KES",

    subtotal:
      Number(
        result.subtotal ??
          0,
      ),

    total:
      Number(
        result.total ??
          0,
      ),

    status:
      String(
        result.status ??
          "pending",
      ),

    serviceMode:
      input.serviceMode,
  };
}
